// UI Components - Responsive Multi-Platform
import React from 'react';

// Button Component - Touch-friendly with responsive sizing
export function Button({ children, variant = 'primary', size = 'md', className = '', disabled = false, ...props }) {
    const baseStyles = 'font-semibold rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 touch-target';
    const variants = {
        primary: 'bg-gradient-to-r from-primary to-primary-light text-white',
        secondary: 'bg-theme-secondary text-theme-primary border border-theme',
        ghost: 'bg-transparent text-theme-primary hover:bg-theme-secondary',
        danger: 'bg-danger text-white',
    };
    const sizes = {
        sm: 'px-4 py-2 text-sm min-h-[36px]',
        md: 'px-6 py-3 min-h-[44px]',
        lg: 'px-8 py-4 text-lg min-h-[52px]',
    };
    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {children}
        </button>
    );
}

// Card Component - Responsive padding
export function Card({ children, className = '', ...props }) {
    return (
        <div className={`bg-theme-card rounded-2xl p-responsive border border-theme ${className}`} {...props}>
            {children}
        </div>
    );
}

// Text Component - Responsive typography
export function Text({ children, variant = 'body', className = '', ...props }) {
    const variants = {
        h1: 'text-responsive-3xl font-bold text-theme-primary block',
        h2: 'text-responsive-2xl font-bold text-theme-primary block',
        h3: 'text-responsive-xl font-semibold text-theme-primary block',
        h4: 'text-responsive-lg font-semibold text-theme-primary block',
        body: 'text-responsive-base text-theme-primary',
        caption: 'text-responsive-sm text-theme-secondary block',
        small: 'text-responsive-xs text-theme-tertiary block',
    };
    return (
        <span className={`${variants[variant]} ${className}`} {...props}>
            {children}
        </span>
    );
}

// Container Component - Responsive width with CSS variables
export function Container({ children, className = '', safe = false, ...props }) {
    return (
        <div className={`app-container bg-theme-bg ${safe ? 'safe-top safe-bottom' : ''} ${className}`} {...props}>
            {children}
        </div>
    );
}

// IconButton - Touch-friendly
export function IconButton({ children, className = '', ...props }) {
    return (
        <button className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors hover:bg-theme-secondary active:scale-95 text-theme-primary touch-target ${className}`} {...props}>
            {children}
        </button>
    );
}

// ProgressBar
export function ProgressBar({ progress, className = '' }) {
    return (
        <div className={`h-1.5 bg-theme-tertiary rounded-full overflow-hidden ${className}`}>
            <div className="h-full bg-gradient-to-r from-primary to-secondary transition-all" style={{ width: `${progress}%` }} />
        </div>
    );
}

// Slider
export function Slider({ value, onChange, min = 1, max = 10, label }) {
    return (
        <div className="space-y-2">
            <input
                type="range"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-2 bg-theme-tertiary rounded-lg appearance-none cursor-pointer accent-primary touch-target"
            />
            <div className="flex justify-between">
                <Text variant="small">{label?.min || min}</Text>
                <Text variant="caption" className="text-primary font-semibold">{value}</Text>
                <Text variant="small">{label?.max || max}</Text>
            </div>
        </div>
    );
}

// OptionButton - Touch-friendly
export function OptionButton({ children, selected, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-3 rounded-xl text-sm transition-all touch-target ${selected
                ? 'bg-primary text-white'
                : 'bg-theme-secondary text-theme-primary border border-theme'
                }`}
        >
            {children}
        </button>
    );
}

// ToggleButton - Touch-friendly
export function ToggleButton({ value, onChange }) {
    return (
        <button
            onClick={() => onChange(!value)}
            className={`w-14 h-8 rounded-full ${value ? 'bg-success' : 'bg-theme-tertiary'} relative transition-colors touch-target`}
        >
            <div className={`absolute top-1 ${value ? 'right-1' : 'left-1'} w-6 h-6 bg-white rounded-full shadow transition-all`} />
        </button>
    );
}

// ScoreRing - Responsive sizing
export function ScoreRing({ score, size = 80, strokeWidth = 6 }) {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = score ? circumference - (score / 100) * circumference : circumference;
    const color = score >= 80 ? '#34C759' : score >= 60 ? '#FF9500' : '#FF3B30';

    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="transform -rotate-90">
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-theme-tertiary" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-bold" style={{ color, fontSize: size / 4 }}>
                    {score || '--'}
                </span>
            </div>
        </div>
    );
}

// QualityIndicator
export function QualityIndicator({ quality }) {
    const color = quality >= 80 ? 'text-success' : quality >= 60 ? 'text-warning' : 'text-danger';
    const label = quality >= 80 ? 'Tốt' : quality >= 60 ? 'Chấp nhận' : 'Kém';

    return (
        <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${color.replace('text-', 'bg-')}`} />
            <Text variant="caption" className={color}>
                Chất lượng: {label} ({quality}%)
            </Text>
        </div>
    );
}

// Toast Component - Responsive positioning
import { useApp } from '../../hooks/useApp';

export function Toast() {
    const { toast } = useApp();
    if (!toast || !toast.show) return null;

    const colors = {
        success: 'bg-success',
        error: 'bg-danger',
        warning: 'bg-warning',
        info: 'bg-info',
    };

    return (
        <div className={`toast-responsive ${colors[toast.type]} text-white px-4 py-3 rounded-xl shadow-lg animate-slide-down`}>
            <span className="font-medium">{toast.message}</span>
        </div>
    );
}

// Barrel export
export { default as Icons } from '../Icons';

